import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Table, Typography, Row, Col, Select, Button, Spin, Tabs, Tag, Space, Card, Tooltip, Input } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, EyeOutlined, SyncOutlined, UserAddOutlined, SearchOutlined } from "@ant-design/icons";
import ProbationReviewModal from "../../components/employee/ProbationReviewModal";

const { Title } = Typography;
const { Option } = Select;

export default function ProbationReviewHr() {
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [loading, setLoading] = useState(false);

    const [searchName, setSearchName] = useState("");
    const [searchEmpCode, setSearchEmpCode] = useState("");

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const [activeTab, setActiveTab] = useState("Pending Approvals");
    const { backUrl, authData } = useContext(AuthContext);

    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [organisationFilter, setOrganisationFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [designationFilter, setDesignationFilter] = useState("");

    // FETCH ORGS
    const fetchOrganisation = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching organisations:", error);
        }
    };

    // FETCH DEPARTMENTS BASED ON ORG ID
    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDepartments(res.data.results || []);
        } catch (error) {
            console.error("Error fetching departments:", error);
        }
    };

    // FETCH DESIGNATIONS BASED ON DEPT ID
    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDesignations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching designations:", error);
        }
    };

    useEffect(() => {
        fetchOrganisation();
    }, []);

    const activeTabToParam = {
        "Pending Approvals": { status: "Pending", is_ready: "true" },
        "Confirmed": { status: "Confirm" },
        "Extended": { status: "Extend" },
        "Separated": { status: "Separated" },
        "Probation List": { is_pool: true }
    };

    // FETCH DATA
    const fetchData = async () => {
        setLoading(true);
        try {
            const params = activeTabToParam[activeTab] || {};
            let url = `${backUrl}/api/probation/probation-reviews/?view_mode=hr`;
            
            if (activeTab === "Probation List") {
                url = `${backUrl}/api/probation/probation-reviews/probation_employees/`;
            } else {
                if (params.status) url += `&overall_status=${params.status}`;
                if (params.is_ready) url += `&is_ready=${params.is_ready}`;
            }

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            setEmployees(res.data.results || []);
            setFilteredEmployees(res.data.results || []);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    // FILTER LOGIC
    useEffect(() => {
        let filtered = [...employees];

        if (organisationFilter) {
            filtered = filtered.filter((item) => {
                const emp = activeTab === "Probation List" ? item : item.employee;
                return emp?.organisation_name === organisationFilter;
            });
        }

        if (departmentFilter) {
            filtered = filtered.filter((item) => {
                const emp = activeTab === "Probation List" ? item : item.employee;
                return emp?.department_name === departmentFilter;
            });
        }

        if (designationFilter) {
            filtered = filtered.filter((item) => {
                const emp = activeTab === "Probation List" ? item : item.employee;
                return emp?.designation_name === designationFilter;
            });
        }

        if (searchEmpCode) {
            filtered = filtered.filter((item) => {
                const emp = activeTab === "Probation List" ? item : item.employee;
                return emp?.emp_code?.toLowerCase().includes(searchEmpCode.toLowerCase());
            });
        }

        if (searchName) {
            filtered = filtered.filter((item) => {
                const emp = activeTab === "Probation List" ? item : item.employee;
                const fullName = `${emp?.first_name} ${emp?.last_name}`.toLowerCase();
                return fullName.includes(searchName.toLowerCase());
            });
        }

        setPagination((prev) => ({ ...prev, current: 1 }));
        setFilteredEmployees(filtered);
    }, [organisationFilter, departmentFilter, designationFilter, searchName, searchEmpCode, activeTab, employees]);

    const handleClearFilters = () => {
        setSearchName("");
        setSearchEmpCode("");
        setOrganisationFilter("");
        setDepartmentFilter("");
        setDesignationFilter("");
        setDepartments([]);
        setDesignations([]);
        fetchData();
    };

    const columns = [
        {
            title: "Employee",
            key: "employee",
            render: (_, record) => {
                const emp = activeTab === "Probation List" ? record : record.employee;
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                backgroundColor: "#0e497b",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: 14,
                                fontWeight: "bold",
                                overflow: "hidden",
                                border: "1px solid #f0f0f0"
                            }}
                        >
                            {emp?.photo ? (
                                <img src={emp.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                                emp?.first_name?.charAt(0)
                            )}
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>
                                {emp?.first_name} {emp?.last_name}
                            </div>
                            <div style={{ fontSize: 11, color: "#888" }}>{emp?.emp_code}</div>
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Department",
            key: "department",
            render: (_, record) => {
                const emp = activeTab === "Probation List" ? record : record.employee;
                return <span style={{ fontSize: 13 }}>{emp?.department_name}</span>;
            },
        },
        {
            title: "Designation",
            key: "designation",
            render: (_, record) => {
                const emp = activeTab === "Probation List" ? record : record.employee;
                return <span style={{ fontSize: 13 }}>{emp?.designation_name}</span>;
            },
        },
        {
            title: "Joining Date",
            key: "joining_date",
            render: (_, record) => {
                const emp = activeTab === "Probation List" ? record : record.employee;
                return <span style={{ fontSize: 13 }}>{emp?.date_of_joining || "—"}</span>;
            },
        },
        {
            title: "Reporting To",
            key: "reporting_to",
            render: (_, record) => {
                const emp = activeTab === "Probation List" ? record : record.employee;
                return <span style={{ fontSize: 12 }}>{emp?.reporting_to_name || "—"}</span>;
            },
        },
        {
            title: activeTab === "Probation List" ? "Probation End" : "Status",
            key: "status",
            render: (_, record) => {
                if (activeTab === "Probation List") {
                    return (
                        <Tag color="blue" style={{ borderRadius: 4 }}>
                            {record.probation_end_date || "Not Set"}
                        </Tag>
                    );
                }
                const statusColors = {
                    Pending: "orange",
                    Confirm: "green",
                    Extend: "blue",
                    Separated: "red",
                };
                return (
                    <Tag color={statusColors[record.overall_status]} style={{ borderRadius: 4 }}>
                        {record.overall_status === "Confirm" ? "Confirmed" : record.overall_status === "Extend" ? "Extended" : record.overall_status}
                    </Tag>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (_, record) => {
                return (
                    <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        size="small"
                        onClick={() => {
                            if (activeTab === "Probation List") {
                                // Wrap employee for modal compatibility
                                setSelectedEmployee({
                                    employee: record,
                                    overall_status: "Probation",
                                    approval_steps: []
                                });
                            } else {
                                setSelectedEmployee(record);
                            }
                            setShowModal(true);
                        }}
                        style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", fontSize: 12, borderRadius: '4px' }}
                    >
                        View Details
                    </Button>
                );
            },
        },
    ];

    return (
        <div style={{ padding: '0 10px' }}>
            <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0, color: '#0e497b' }}>Team Employment Status</Title>
            </div>

            <Card style={{ borderRadius: '8px', marginBottom: '20px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    type="card"
                    className="custom-tabs"
                    items={[
                        { key: "Pending Approvals", label: "Pending Approvals", icon: <ClockCircleOutlined /> },
                        { key: "Confirmed", label: "Confirmed", icon: <CheckCircleOutlined /> },
                        { key: "Extended", label: "Extended", icon: <SyncOutlined /> },
                        { key: "Separated", label: "Separated", icon: <CloseCircleOutlined /> },
                        { key: "Probation List", label: "Probation List", icon: <UserAddOutlined /> },
                    ]}
                />

                <Row gutter={[12, 12]} style={{ marginTop: 20 }}>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Search Name"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Search Emp Code"
                            value={searchEmpCode}
                            onChange={(e) => setSearchEmpCode(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Organisation"
                            style={{ width: '100%' }}
                            allowClear
                            onChange={(val) => {
                                setOrganisationFilter(val);
                                setDepartmentFilter("");
                                setDesignationFilter("");
                                if (val) {
                                    const orgObj = Organisations.find((o) => o.name === val);
                                    if (orgObj) fetchDepartments(orgObj.id);
                                }
                            }}
                            value={organisationFilter || undefined}
                        >
                            {Organisations.map((org) => (
                                <Option key={org.id} value={org.name}>{org.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Department"
                            style={{ width: '100%' }}
                            allowClear
                            value={departmentFilter || undefined}
                            onChange={(val) => {
                                setDepartmentFilter(val);
                                setDesignationFilter("");
                                const deptObj = departments.find((d) => d.name === val);
                                if (deptObj) fetchDesignations(deptObj.id);
                            }}
                            disabled={!organisationFilter}
                        >
                            {departments.map((dept) => (
                                <Option key={dept.id} value={dept.name}>{dept.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Designation"
                            style={{ width: '100%' }}
                            allowClear
                            value={designationFilter || undefined}
                            onChange={(val) => setDesignationFilter(val)}
                            disabled={!departmentFilter}
                        >
                            {designations.map((desig) => (
                                <Option key={desig.id} value={desig.name}>{desig.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Button
                            onClick={handleClearFilters}
                            block
                            style={{ borderRadius: '4px' }}
                        >
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
            </Card>

            <Card style={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }} bodyStyle={{ padding: 0 }}>
                <Table
                    columns={columns}
                    dataSource={filteredEmployees}
                    rowKey={(record) => record.id}
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: filteredEmployees.length,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        onChange: (page, pageSize) => {
                            setPagination({ current: page, pageSize });
                        },
                    }}
                    bordered={false}
                    className="global-ant-table"
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white", fontWeight: '500', padding: '12px 16px' }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </Card>

            {showModal && (
                <ProbationReviewModal
                    visible={showModal}
                    employee={selectedEmployee}
                    onClose={() => setShowModal(false)}
                />
            )}

            <style>{`
                .custom-tabs .ant-tabs-nav::before {
                    border-bottom: 1px solid #f0f0f0;
                }
                .custom-tabs .ant-tabs-tab {
                    background: transparent !important;
                    border: none !important;
                    margin-right: 20px !important;
                    padding: 8px 0 !important;
                }
                .custom-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
                    color: #0e497b !important;
                    font-weight: 600 !important;
                }
                .custom-tabs .ant-tabs-ink-bar {
                    background: #0e497b !important;
                    height: 3px !important;
                }
                .global-ant-table .ant-table-tbody > tr > td {
                    padding: 12px 16px;
                }
                .global-ant-table .ant-table-tbody > tr:hover > td {
                    background-color: #f8fbff !important;
                }
            `}</style>
        </div>
    );
}